import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { TextField, Checkbox, FormControlLabel, IconButton } from '@material-ui/core';
import { Delete, Add } from '@material-ui/icons';
import { connect } from 'react-redux';
import api from 'api';
import { Button } from 'components/common/Button/Button';
import { selectSelectedSupplierId, selectSelectedSupplier } from 'store/suppliersList/Selector';
import { editSupplierData as editSupplierDataAction, getSuppliersList } from 'store/suppliersList/ActionCreator';
import { startLoading, finishLoading } from 'hooks/useProgress';
import { Supplier, SupplierId } from 'store/suppliersList/types';
import { AppState } from 'store/RootReducer';
import { Review } from 'components/SupplierCard/Tabs/ReviewTab/types';
import { showSnackbar } from 'hooks/useSnackbar';
import { useCanEditSrm } from 'hooks/useHasRole';
import cn from './ReviewTab.module.scss';

type MapStateToProps = {
  selectedSupplierId: SupplierId;
  selectedSupplier?: Supplier;
};

type MapDispatchToProps = {
  editSupplierData: (supplierId: string, supplier: Supplier) => void;
  refreshSuppliers: () => void;
};

type ReviewTabProps = MapStateToProps & MapDispatchToProps;

const fields = {
  GENERAL: 'general',
  EQUIPMENT: 'equipment',
  ENVIRONMENT: 'environment',
  COVERAGE: 'coverage',
  IT: 'it',
  DEVELOPMENT: 'development',
  PLATFORM: 'platform',
};

const initialReview: Review = {
  id: 0,
  general: '',
  equipment: '',
  environment: '',
  coverage: '',
  it: '',
  development: '',
  platform: '',
  supplierCardId: '',
};

const scoredPreferences = [
  { key: 'iso9001', label: 'ISO 9001' },
  { key: 'iso14001', label: 'ISO 14001' },
  { key: 'iso45001', label: 'ISO 45001' },
  { key: 'edi', label: 'EDI' },
  { key: 'realtimeTracking', label: 'Realtime Tracking' },
] as const;

const trackedFields = [
  { key: 'ecoVadis', label: 'EcoVadis' },
  { key: 'acceptedCodeOfConduct', label: 'Accepted Code of Conduct' },
] as const;

const allPrefKeys = [...scoredPreferences, ...trackedFields];

const ReviewTabComponent = React.memo(
  ({ selectedSupplierId, selectedSupplier, editSupplierData, refreshSuppliers }: ReviewTabProps) => {
    const [review, setReview] = useState<Review>(initialReview);
    const [prefs, setPrefs] = useState<Record<string, boolean>>({});
    const [otherCertificates, setOtherCertificates] = useState('');
    const [extSystems, setExtSystems] = useState<Array<{ id: number; externalSystemName: string; externalSystemId: string }>>([]);
    const [newExtName, setNewExtName] = useState('');
    const [newExtId, setNewExtId] = useState('');
    const canEdit = useCanEditSrm();

    const requestData = useCallback(async () => {
      startLoading();
      try {
        const dbReview = await api.getReview(selectedSupplierId);
        setReview(dbReview || initialReview);
        const systems = await api.getExternalSystems(selectedSupplierId);
        setExtSystems(systems || []);
      } catch (err) {}
      finishLoading();
    }, [selectedSupplierId]);

    useEffect(() => {
      if (selectedSupplier) {
        const p: Record<string, boolean> = {};
        allPrefKeys.forEach(f => { p[f.key] = (selectedSupplier as any)[f.key] || false; });
        setPrefs(p);
        setOtherCertificates(selectedSupplier.otherCertificates || '');
      }
    }, [selectedSupplier]);

    function handleInputChange(
      event: ChangeEvent<HTMLInputElement>,
      field: string
    ): void {
      setReview({
        ...review,
        [field]: event.target.value,
      });
    }

    function handlePrefChange(key: string, checked: boolean): void {
      setPrefs({ ...prefs, [key]: checked });
    }

    async function handleAddExtSystem(): Promise<void> {
      if (!newExtName.trim() || !newExtId.trim()) return;
      startLoading();
      try {
        await api.addExternalSystem(selectedSupplierId, { externalSystemName: newExtName, externalSystemId: newExtId });
        setNewExtName('');
        setNewExtId('');
        const systems = await api.getExternalSystems(selectedSupplierId);
        setExtSystems(systems || []);
        showSnackbar({ message: 'External system added.', type: 'success' });
      } catch (err) {}
      finishLoading();
    }

    async function handleDeleteExtSystem(id: number): Promise<void> {
      startLoading();
      try {
        await api.deleteExternalSystem(selectedSupplierId, id);
        setExtSystems(extSystems.filter(s => s.id !== id));
        showSnackbar({ message: 'External system removed.', type: 'success' });
      } catch (err) {}
      finishLoading();
    }

    async function handleSaveButton(): Promise<void> {
      if (!selectedSupplier) return;
      startLoading();
      try {
        await api.editReview(selectedSupplierId, review);
        const updatedSupplier = { ...selectedSupplier, ...prefs, otherCertificates };
        await editSupplierData(selectedSupplierId, updatedSupplier);
        refreshSuppliers();
        showSnackbar({
          message: 'Review was updated.',
          type: 'success',
        });
      } catch (err) {}
      finishLoading();
    }

    useEffect(() => {
      if (selectedSupplierId) {
        requestData();
      }
    }, [selectedSupplierId, requestData]);

    return (
      <div className={cn.reviewContainer}>
        <div className={cn.preferencesSection}>
          <div className={cn.sectionTitle}>Preferences (scored)</div>
          <div className={cn.preferencesGrid}>
            {scoredPreferences.map(f => (
              <FormControlLabel
                key={f.key}
                control={
                  <Checkbox
                    checked={prefs[f.key] || false}
                    onChange={(e) => handlePrefChange(f.key, e.target.checked)}
                    color="primary"
                    disabled={!canEdit}
                  />
                }
                label={f.label}
              />
            ))}
          </div>
          <div className={cn.sectionTitle + ' ' + cn.trackedTitle}>Tracked (not scored)</div>
          <div className={cn.preferencesGrid}>
            {trackedFields.map(f => (
              <FormControlLabel
                key={f.key}
                control={
                  <Checkbox
                    checked={prefs[f.key] || false}
                    onChange={(e) => handlePrefChange(f.key, e.target.checked)}
                    color="default"
                    disabled={!canEdit}
                  />
                }
                label={f.label}
              />
            ))}
          </div>
          <div className={cn.sectionTitle + ' ' + cn.trackedTitle}>Other Certificates</div>
          <TextField
            value={otherCertificates}
            onChange={(e) => setOtherCertificates(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="e.g. SQAS, GDP, AEO, TAPA..."
            disabled={!canEdit}
          />
        </div>
        <div className={cn.preferencesSection}>
          <div className={cn.sectionTitle}>External Platforms</div>
          {extSystems.map(s => (
            <div key={s.id} className={cn.extRow}>
              <span className={cn.extLabel}>{s.externalSystemName}: {s.externalSystemId}</span>
              {canEdit && (
                <IconButton size="small" onClick={() => handleDeleteExtSystem(s.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </div>
          ))}
          {canEdit && (
            <div className={cn.extRow}>
              <TextField
                label="System (e.g. TimoCom)"
                value={newExtName}
                onChange={(e) => setNewExtName(e.target.value)}
                variant="outlined"
                size="small"
              />
              <TextField
                label="ID"
                value={newExtId}
                onChange={(e) => setNewExtId(e.target.value)}
                variant="outlined"
                size="small"
              />
              <IconButton size="small" color="primary" onClick={handleAddExtSystem}>
                <Add />
              </IconButton>
            </div>
          )}
        </div>
        <div className={cn.row}>
          <TextField
            label="General"
            name="general"
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleInputChange(event, fields.GENERAL)
            }
            value={review.general ?? ''}
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            variant="outlined"
            InputProps={{
              classes: {
                root: cn.inputRoot,
                notchedOutline: cn.noBorderRadius,
              },
            }}
            InputLabelProps={{
              shrink: !!review.general,
            }}
          />
          <TextField
            label="Equipment"
            name="equipment"
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleInputChange(event, fields.EQUIPMENT)
            }
            value={review.equipment ?? ''}
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            variant="outlined"
            InputProps={{
              classes: {
                root: cn.inputRoot,
                notchedOutline: cn.noBorderRadius,
              },
            }}
             InputLabelProps={{
               shrink: !!review.equipment,
             }}
          />
        </div>
        <div className={cn.row}>
          <TextField
            label="Environment"
            name="environment"
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleInputChange(event, fields.ENVIRONMENT)
            }
            value={review.environment ?? ''}
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            variant="outlined"
            InputProps={{
              classes: {
                root: cn.inputRoot,
                notchedOutline: cn.noBorderRadius,
              },
            }}
            InputLabelProps={{
              shrink: !!review.environment,
            }}
          />
          <TextField
            label="Coverage"
            name="coverage"
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleInputChange(event, fields.COVERAGE)
            }
            value={review.coverage ?? ''}
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            variant="outlined"
            InputProps={{
              classes: {
                root: cn.inputRoot,
                notchedOutline: cn.noBorderRadius,
              },
            }}
            InputLabelProps={{
              shrink: !!review.coverage,
            }}
          />
        </div>
        <div className={cn.row}>
          <TextField
            label="IT"
            name="it"
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleInputChange(event, fields.IT)
            }
            value={review.it ?? ''}
            fullWidth
            multiline
            minRows={3}
            maxRows={3}
            variant="outlined"
            InputProps={{
              classes: {
                root: cn.inputRoot,
                notchedOutline: cn.noBorderRadius,
              },
            }}
            InputLabelProps={{
              shrink: !!review.it,
            }}
          />
          <TextField
            label="Development"
            name="development"
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleInputChange(event, fields.DEVELOPMENT)
            }
            value={review.development ?? ''}
            fullWidth
            multiline
            minRows={3}
            maxRows={5}
            variant="outlined"
            InputProps={{
              classes: {
                root: cn.inputRoot,
                notchedOutline: cn.noBorderRadius,
              },
            }}
            InputLabelProps={{
              shrink: !!review.development,
            }}
          />
        </div>
        <div className={cn.row}>
          <TextField
            label="Platform (SENT/DIWASS/other)"
            name="platform"
            onChange={(event: ChangeEvent<HTMLInputElement>): void =>
              handleInputChange(event, fields.PLATFORM)
            }
            value={review.platform ?? ''}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            variant="outlined"
            InputProps={{
              classes: {
                root: cn.inputRoot,
                notchedOutline: cn.noBorderRadius,
              },
            }}
            InputLabelProps={{
              shrink: !!review.platform,
            }}
          />
        </div>
        <div className={cn.buttonRow}>
          <Button onClick={handleSaveButton}>Save</Button>
        </div>
      </div>
    );
  }
);

const mapStateToProps = (state: AppState): MapStateToProps => ({
  selectedSupplierId: selectSelectedSupplierId(state),
  selectedSupplier: selectSelectedSupplier(state),
});

const mapDispatchToProps: MapDispatchToProps = {
  editSupplierData: editSupplierDataAction,
  refreshSuppliers: getSuppliersList,
};

export const ReviewTab = connect(mapStateToProps, mapDispatchToProps)(ReviewTabComponent);
